import { useCallback, useEffect, useState } from "react";
import LegoLogo from "assets/LegoLogo.svg";
import { calculateEstimate } from "lib/calculateEstimate";
import CryptoJS from "crypto-js";

// Import screens
import HoldingScreen from "./screens-simplified/holding-screen";
import PlacePackageScreen from "./screens-simplified/place-package-screen";
import WeightDisplayScreen from "./screens-simplified/weight-display-screen";
import QrCodeScreen from "./screens-simplified/qr-code-screen";
import TNCS from "./screens-simplified/have-you-accepted-screen";

const { REACT_APP_API } = process.env;
export const SIGNING_KEY = `cdjedlehccbhnbukhtdlrnlifgtdhlnltglifgtdhlctlhuejt`;

export default function DropoffKiosk() {
  // Navigation - simplified to just 4 screens
  const [screenIndex, setScreenIndex] = useState("holding");
  const [, setPreviousScreen] = useState(null);

  // Scale input processing
  const [number, setNumber] = useState("");
  const [inputWeight, setInputWeight] = useState(0);
  const weight = inputWeight;
  const estimate = calculateEstimate(weight.toFixed(2));

  // For QR code generation
  const signedWeight = CryptoJS.SHA256(`${weight}_${SIGNING_KEY}`).toString(
    CryptoJS.enc.Hex
  );

  // Handle weight conversion from kg to lbs
  const convertWeight = (weight) => {
    const weightInlbs = weight * 2.20462;
    return weightInlbs;
  };

  // Auto reload at specific times
  useEffect(() => {
    const checkAndReload = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      if (
        (currentHour === 6 && currentMinute === 0) ||
        (currentHour === 22 && currentMinute === 0)
      ) {
        window.location.reload();
      }
    };

    const intervalId = setInterval(checkAndReload, 60000);
    return () => clearInterval(intervalId);
  }, []);

  // Handle keyboard input from scale
  const handleKeyPress = useCallback(
    (event) => {
      const keyPressed = event.key;
      if (!isNaN(keyPressed) || keyPressed === ".") {
        setNumber((prev) => (prev + keyPressed).slice(0, 6));
      } else if (keyPressed === "Enter") {
        const newWeight = parseFloat(number).toFixed(2);
        setNumber("");

        if (!isNaN(newWeight)) {
          const convertedWeight = convertWeight(newWeight);
          setInputWeight(parseFloat(convertedWeight));

          // Send to KV
          const timestamp = new Date().toISOString();
          getLocation().then((location) => {
            sendToKV({ weight: newWeight, location, timestamp });
          });
        }
      }
    },
    [number]
  );

  // Get device location
  const getLocation = async () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          () => {
            resolve();
          }
        );
      }
    });
  };

  // Send data to backend
  const sendToKV = async (data) => {
    try {
      await fetch(`${REACT_APP_API}/audit/scale`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error("Error sending data to KV:", error);
    }
  };

  // Setup keyboard listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      handleKeyPress(event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyPress]);

  // Screen navigation
  const navigate = (newScreenIndex) => {
    setPreviousScreen(screenIndex);
    setScreenIndex(newScreenIndex);
  };

  // Screen map for the 4 screens
  const screenMap = {
    holding: {
      element: (
        <HoldingScreen setInputWeight={setInputWeight} navigate={navigate} />
      ),
      bgColor: "blue",
    },
    tncs: {
      element: <TNCS navigate={navigate} setInputWeight={setInputWeight} />,
      bgColor: "green",
    },
    placePackage: {
      element: (
        <PlacePackageScreen
          navigate={navigate}
          setInputWeight={setInputWeight}
        />
      ),
      bgColor: "blue",
    },
    weightDisplay: {
      compressed: true,
      element: (
        <WeightDisplayScreen
          navigate={navigate}
          weight={weight}
          estimate={estimate}
          setInputWeight={setInputWeight}
        />
      ),
      bgColor: "blue-200",
    },
    qrCode: {
      compressed: true,
      element: (
        <QrCodeScreen
          navigate={navigate}
          weight={weight}
          estimate={estimate}
          signedWeight={signedWeight}
          takebackId={`dropoff_${Date.now()}`}
        />
      ),
      bgColor: "blue-200",
    },
  };

  const isCompressed = screenMap[screenIndex].compressed;

  return (
    <div
      className={`w-screen h-screen ${
        screenMap[screenIndex].bgColor === "blue-200"
          ? "bg-[#e2f3ff]"
          : screenMap[screenIndex].bgColor === "blue"
            ? "bg-blue"
            : "bg-" + screenMap[screenIndex].bgColor
      } flex flex-col items-center overflow-hidden font-bold`}
    >
      {/* Container optimized for 16:10 resolution (1280x800) */}
      <div className="w-full h-full pb-[3vh] flex flex-col items-center relative">
        <img
          src={LegoLogo}
          alt="LEGO logo"
          className={`w-[120px] relative z-10 ${isCompressed ? "mt-[70px]" : "mt-[110px]"}`}
        />
        {screenMap[screenIndex].element}
      </div>
      <p
        className={`absolute bottom-[16px] font-[400] text-[12px] ${screenMap[screenIndex].bgColor === "blue-200" ? "text-black" : "text-white"}`}
      >
        LEGO, the LEGO logo and the Minifigure are trademarks of the LEGO Group.
        ©2025 The LEGO Group.
      </p>
    </div>
  );
}
